import React, { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  FormControlLabel,
  Checkbox,
  Link,
} from '@mui/material';
import { CaptivePortalTemplateProps } from '../types';
import { CaptivePortalContainer, isCaptivePortalFormDataValid } from './container';

export const MinimalTemplateDataKeys = [
  'logoUrl',
  'header',
  'content',
  'buttonText',
  'showName',
  'showEmail',
  'requireName',
  'requireEmail',
  'requireAcceptMarketing',
  'termsAndConditionsUrl',
  'privacyPolicyUrl',
  'contentTextColor',
  'fieldTextColor',
  'fieldBorderColor',
  'buttonColor',
  'buttonTextColor',
  'primaryColor',
  'headerTextColor',
] as const;

export const MinimalTemplate: React.FC<
  CaptivePortalTemplateProps<(typeof MinimalTemplateDataKeys)[number]>
> = (props) => {
  const { data, formData, onFormChange, onSubmit, isSubmitting = false } = props;
  const { t } = useTranslation();

  const handleInputChange =
    (field: keyof typeof formData) => (e: React.ChangeEvent<HTMLInputElement>) => {
      onFormChange({
        ...formData,
        [field]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
      });
    };

  const validateForm = (): boolean => {
    // Check required fields
    if (data.requireEmail && (!formData.email || !formData.email.trim())) {
      return false;
    }
    if (data.requireName) {
      if (!formData.firstName || !formData.firstName.trim()) {
        return false;
      }
      if (!formData.lastName || !formData.lastName.trim()) {
        return false;
      }
    }
    // Check required marketing acceptance
    if (data.requireAcceptMarketing && !formData.acceptedMarketing) {
      return false;
    }
    // Check email format if email is provided
    if (formData.email && formData.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        return false;
      }
    }
    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isCaptivePortalFormDataValid(data, formData)) {
      onSubmit();
    }
  };

  return (
    <CaptivePortalContainer {...props}>
      <Box sx={{ width: '100%', maxWidth: 'sm' }}>
        {/* Logo */}
        {data.logoUrl && (
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box
              component="img"
              src={data.logoUrl}
              alt={t('logo', 'Logo')}
              sx={{ height: 48, maxWidth: '100%' }}
            />
          </Box>
        )}

        {/* Header */}
        {data.header && (
          <Typography
            variant="h5"
            component="h1"
            sx={{
              textAlign: 'center',
              mb: 1,
              fontWeight: 300,
              color: data.headerTextColor || 'text.primary',
            }}
          >
            {data.header}
          </Typography>
        )}

        {/* Content */}
        {data.content && (
          <Typography
            variant="body2"
            sx={{
              textAlign: 'center',
              mb: 4,
              color: data.contentTextColor || 'text.secondary',
              whiteSpace: 'pre-wrap',
            }}
          >
            {data.content}
          </Typography>
        )}

        {/* Form */}
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
        >
          {data.showEmail && (
            <StyledTextField
              data={data}
              type="email"
              placeholder={t('email', 'Email')}
              value={formData.email || ''}
              onChange={handleInputChange('email')}
              variant="standard"
              fullWidth
              required={data.requireEmail}
            />
          )}

          {data.showName && (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <StyledTextField
                  data={data}
                  type="text"
                  placeholder={t('firstName', 'First name')}
                  value={formData.firstName || ''}
                  onChange={handleInputChange('firstName')}
                  variant="standard"
                  fullWidth
                  required={data.requireName}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <StyledTextField
                  data={data}
                  type="text"
                  placeholder={t('lastName', 'Last name')}
                  value={formData.lastName || ''}
                  onChange={handleInputChange('lastName')}
                  variant="standard"
                  fullWidth
                  required={data.requireName}
                />
              </Grid>
            </Grid>
          )}

          {(data.termsAndConditionsUrl || data.requireAcceptMarketing) && (
            <Box display="flex" mt={1} flexDirection="column">
              {data.termsAndConditionsUrl && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.acceptedTerms || false}
                      onChange={handleInputChange('acceptedTerms')}
                      sx={{
                        color: data.fieldBorderColor || 'grey.300',
                        '&.Mui-checked': {
                          color: data.fieldBorderColor || data.contentTextColor || 'primary.main',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography
                      variant="body2"
                      sx={{ color: data.contentTextColor || 'text.primary' }}
                    >
                      {t('acceptTerms', 'I accept the')}{' '}
                      <Link
                        href={data.termsAndConditionsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          color: data.contentTextColor || 'primary.main',
                          textDecoration: 'none',
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        {t('termsConditions', 'Terms & Conditions')}
                      </Link>
                      {data.privacyPolicyUrl && (
                        <>
                          {' '}
                          {t('and', 'and')}{' '}
                          <Link
                            href={data.privacyPolicyUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              color: data.contentTextColor || 'primary.main',
                              textDecoration: 'none',
                              '&:hover': {
                                textDecoration: 'underline',
                              },
                            }}
                          >
                            {t('privacyPolicy', 'Privacy Policy')}
                          </Link>
                        </>
                      )}
                      .
                    </Typography>
                  }
                  sx={{ alignItems: 'center' }}
                />
              )}

              {data.requireAcceptMarketing && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.acceptedMarketing || false}
                      onChange={handleInputChange('acceptedMarketing')}
                      sx={{
                        color: data.fieldBorderColor || 'grey.300',
                        '&.Mui-checked': {
                          color: data.fieldBorderColor || data.contentTextColor || 'primary.main',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography
                      variant="body2"
                      sx={{ color: data.contentTextColor || 'text.primary' }}
                    >
                      {t(
                        'acceptMarketing',
                        'I would like to receive marketing communications and promotional offers',
                      )}
                    </Typography>
                  }
                  sx={{ alignItems: 'center' }}
                />
              )}
            </Box>
          )}

          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting || !isCaptivePortalFormDataValid(data, formData)}
            fullWidth
            sx={{
              mt: 2,
              py: 2,
              fontSize: '0.875rem',
              fontWeight: 500,
              letterSpacing: '0.05em',
              background: data.buttonColor || '#000000',
              color: data.buttonTextColor || 'white',
              boxShadow: 0,
              borderRadius: 0,
              '&:hover': {
                background: data.buttonColor || '#333333',
                boxShadow: 0,
              },
              '&:disabled': {
                opacity: 0.7,
                background: data.buttonColor || '#000000',
                color: data.buttonTextColor || 'white',
              },
              transition: 'opacity 0.2s ease-in-out',
            }}
          >
            {isSubmitting ? '...' : data.buttonText || t('continue', 'CONTINUE')}
          </Button>
        </Box>
      </Box>
    </CaptivePortalContainer>
  );
};

function StyledTextField(
  props: ComponentProps<typeof TextField> & {
    data: CaptivePortalTemplateProps<(typeof MinimalTemplateDataKeys)[number]>['data'];
  },
) {
  const { data } = props;
  return (
    <TextField
      {...props}
      sx={{
        '& .MuiInput-underline:before': {
          borderBottomColor: data.fieldBorderColor || 'grey.300',
        },
        '& .MuiInput-underline:after': {
          borderBottomColor: data.buttonColor || data.primaryColor || 'text.primary',
        },
        '& .MuiInputBase-input': {
          py: 1.5,
          fontSize: '0.875rem',
          color: data.fieldTextColor || data.contentTextColor || 'text.primary',
        },
        '& .MuiInputBase-input::placeholder': {
          color: 'text.disabled',
          opacity: 1,
        },
      }}
    />
  );
}
